import { useEffect, useState } from "react";
import { Button, Col, Form, Input, message, Row, Switch } from "antd";
import { InfiniteSelect } from "../../../../components/infiniteSelect";
import { useGetData } from "../../../../Network/useGetQueryData";
import { useParams } from "react-router-dom";
import { BranchCards } from "../../BranchCard";
import { usePostData } from "../../../../Network/usePostData";
import { usePutData } from "../../../../Network/usePutData";
import { SearchIcon } from "../../../../Icons/SearchIcon";
import MapPicker from "../../../../components/MapComponent";
import { Loading } from "../../../../components/Loading";
import { maxLength, required } from "../../../../utils/form-validations";
import { AutoCompleteSearch } from "../../../../components/AddressControl";
import { useDeleteData } from "../../../../Network/useDeleteData";
import { useTranslation } from "react-i18next";
import "./style.scss";

export const BranchStep = ({
  onChangeStep,
}: {
  onChangeStep?: (step: number) => void;
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>();
  const [selectedBranch, setSelectedBranch] = useState<any>();
  const [branchSearchKey, setBranchSearchKey] = useState<any>();
  const [deletedBranch, setDeletedBranch] = useState<any>();
  const [editView, setEditView] = useState<boolean>(id ? false : true);
  const [location, setLocation] = useState<{ lat: number; lng: number }>();
  const [form] = Form.useForm();

  const {
    data: Branches,
    isLoading,
    refetch,
  } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-provider-branches"],
    url: `service-provider-branch?serviceProviderId=${id}&offset=0&limit=30&withDeleted=false`,
    params: { searchKey: branchSearchKey },
    options: {
      enabled: !!id,
      queryKey: ["service-provider-branches", { branchSearchKey }],
    },
  });

  const { mutateAsync } = usePostData({
    url: "service-provider-branch",
    options: {
      onSuccess: () => {
        form.resetFields();
        setEditView(false);
        refetch();
      },
      onError: (error) => {},
    },
  });

  const { mutate } = usePutData<{ message: string }, { name: string }>({
    url: `service-provider-branch`,
    options: {
      onSuccess: () => {
        form.resetFields();
        setEditView(false);
        refetch();
      },
      onError: (error) => {},
    },
  });

  const { mutate: deleteItem } = useDeleteData({
    url: `service-provider-branch/${deletedBranch}`,
    options: {
      onSuccess: () => {
        message.success(t("branches.list.deleteSuccess"));
        setDeletedBranch("");
        refetch();
      },
      onError: (error) => {
        console.error("Failed to delete item:", error);
      },
    },
  });

  useEffect(() => {
    if (!deletedBranch) return;
    deleteItem();
  }, [deletedBranch]);

  const [cityId, setCityId] = useState<string | undefined>();

  const handleAddBranch = async (values: any) => {
    setLoading(true);
    try {
      const branchData = {
        ...values,
        serviceProviderId: +id,
        location: { ...values?.location, countryId: 1 },
        status: "active",
      };

      if (selectedBranch)
        mutate({
          ...branchData,
          id: selectedBranch?.id,
          location: {
            ...branchData?.location,
            id: selectedBranch?.location?.id,
          },
        });
      else {
        await mutateAsync(branchData);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSetBranchToEdit = (branch: any) => {
    setSelectedBranch(branch);
    if (!editView) setEditView(true);
    form.setFieldsValue({ ...branch });
    setLocation({
      lat: branch?.location?.latitude,
      lng: branch?.location?.longitude,
    });
  };

  return (
    <>
      {editView && (
        <Form
          layout="vertical"
          className="branch-form-data"
          form={form}
          onFinish={handleAddBranch}
        >
          <p className="form-section-title">{t("branches.form.addBranch")}</p>
          <Row gutter={32}>
            <Col lg={24} xl={12}>
              <Form.Item
                rules={[required, maxLength(255)]}
                label={t("branches.form.branchName")}
                name={["name", "en"]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col lg={24} xl={12}>
              <Form.Item
                rules={[required, maxLength(255)]}
                label={t("branches.form.branchNameAr")}
                name={["name", "ar"]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col lg={24} xl={12}>
              <Form.Item
                rules={[
                  required,
                  {
                    max: 15,
                    message: t("branches.form.phoneMaxLength"),
                  },
                ]}
                label={t("branches.form.branchPhone")}
                name="phone"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col lg={24} xl={12}>
              <Form.Item label={t("branches.form.setPrimary")} name="isPrimary">
                <Switch />
              </Form.Item>
            </Col>

            <Col lg={24} xl={12}>
              <Form.Item
                rules={[
                  {
                    type: "email",
                    message: t("branches.form.emailInvalid"),
                  },
                  maxLength(255),
                ]}
                label={t("branches.form.email")}
                name="email"
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xl={24}>
              <Row>
                <Col lg={24} xl={12}>
                  <Form.Item
                    rules={[required, maxLength(3000)]}
                    label={t("branches.form.address")}
                    name={["location", "address"]}
                  >
                    <AutoCompleteSearch
                      onSelectData={(data) => {
                        setLocation(data.location);
                        form.setFieldsValue({
                          location: {
                            latitude: location.lat,
                            longitude: location.lng,
                          },
                        });
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t("branches.form.city")}
                    rules={[required]}
                    name={["location", "cityId"]}
                  >
                    <InfiniteSelect
                      onChange={setCityId}
                      url="location-item?type=city&parentId=1&withDeleted=false"
                      optionsKeys={{ label: "name", value: "id" }}
                      params={
                        cityId ? { countryId: 1, offset: 0, limit: 20 } : {}
                      }
                      queryKey={["countryId"]}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t("branches.form.area")}
                    rules={[required]}
                    name={["location", "areaId"]}
                  >
                    <InfiniteSelect
                      url={`location-item?type=area&parentId=${cityId}&withDeleted=false`}
                      optionsKeys={{ label: "name", value: "id" }}
                      params={cityId ? { cityId: cityId } : {}}
                      queryKey={["cityId", cityId]}
                      disabled={!cityId}
                      shouldFetch={!!cityId}
                    />
                  </Form.Item>
                </Col>

                <Col
                  lg={24}
                  xl={12}
                  style={{ paddingInlineStart: "15px", paddingTop: "32px" }}
                >
                  <MapPicker
                    onChange={(location) => {
                      form.setFieldsValue({
                        location: {
                          latitude: location.lat,
                          longitude: location.lng,
                        },
                      });
                    }}
                    disableDefaultUI={true}
                    disableZoom={true}
                    defaultLocation={location}
                  />
                </Col>
              </Row>
            </Col>

            <Col lg={24} xl={12}>
              <Form.Item
                label={t("branches.form.longitude")}
                name={["location", "longitude"]}
              >
                <Input
                  type="number"
                  onChange={(e) =>
                    setLocation({ ...location, lng: +e.target.value })
                  }
                />
              </Form.Item>
            </Col>

            <Col lg={24} xl={12}>
              <Form.Item
                label={t("branches.form.latitude")}
                name={["location", "latitude"]}
              >
                <Input
                  type="number"
                  onChange={(e) =>
                    setLocation({ ...location, lat: +e.target.value })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="steps-action-footer">
            <Button loading={loading} type="primary" htmlType="submit">
              {t("shared.save")}
            </Button>
          </div>
        </Form>
      )}

      <div className="branches-list">
        <div className="form-list-header">
          <p>{t("branches.list.title")}</p>

          <div className="header-search">
            {!editView && (
              <Button onClick={() => setEditView(true)} type="primary">
                {t("shared.add")}
              </Button>
            )}
            <Input
              size="large"
              placeholder={t("branches.search.placeholder")}
              prefix={<SearchIcon />}
              onChange={(e) => setBranchSearchKey(e.target.value)}
            />
          </div>
        </div>
        <Row gutter={32}>
          {isLoading ? (
            <Loading />
          ) : !Branches?.data?.items?.length ? (
            <div
              style={{ textAlign: "center", margin: "20px 0", width: "100%" }}
            >
              {t("branches.list.empty")}
            </div>
          ) : (
            Branches?.data?.items.map((branch) => (
              <Col key={branch.id as number} lg={24} xl={12}>
                <BranchCards
                  isEdit={selectedBranch?.id === branch.id}
                  onCardClick={handleSetBranchToEdit}
                  data={branch}
                  deleteItem={setDeletedBranch}
                  onChangePrimary={async (data) => {
                    mutate({ ...data, isPrimary: true });
                    setTimeout(() => {
                      refetch();
                    }, 500);
                  }}
                />
              </Col>
            ))
          )}
        </Row>
      </div>
    </>
  );
};
